import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router-dom';
import { getHaito } from 'config/api';
import { useRouting } from 'hooks/useRouting';
import { getDividendStorage, setDividendStorage } from 'hooks/useDividend';
import { formatNumber, formatDate } from 'utils/formatter';
import { RecurrentModal, ModalID } from 'routes/components/RecurrentModal';
import Loading from 'components/commons/loading/Loading';
import { PremiumCheck2 as PremiumCheck } from 'components/commons/premium-check/PremiumCheck';
import { DevidendPremium } from 'components/features/dividendPrediction/DividendPremium';
import { DividendClassification } from 'components/features/dividendPrediction/DividendClassification';
import {
  DividendTransition,
  getSummaryDividendHaito,
} from 'components/features/dividendPrediction/DividendTransition';
import {
  DividendNoData,
  DividendPredictionLoading,
} from 'components/features/dividendPrediction/DividendError';
import { isIosApp, isPremium } from 'components/features/utils/userStatus';
import { convertyyyymmToDate } from './DividendDeposit';
import { HightDividendList } from './DividendDetailDeposit';

export const UpdateTimeComment = () => {
  return (
    <div className="pt-2 pl-4 text-xs text-bluegray-500 bg-bluegray-100">
      ※ 配当情報は毎日1:00頃と13:00頃に更新されます。
    </div>
  );
};

export const DividendHeader = ({
  type,
}: {
  type: 'prediction' | 'deposit';
}) => {
  const routing = useRouting();
  const goDividendPrediction = (): void => {
    routing.pushRouting({ path: `/dividendprediction` }, true);
  };
  const goDividendDeposit = (): void => {
    routing.pushRouting({ path: `/dividenddeposit` }, true);
  };

  return (
    <>
      <div className="mt-2 px-4 border-b-3 border-sky-550">
        <div
          className="grid gap-2"
          style={{ gridAutoFlow: 'column', gridAutoColumns: '1fr' }}>
          <div
            className="rounded-t overflow-hidden cursor-pointer"
            onClick={() => goDividendPrediction()}>
            <div
              className={`py-2 font-bold flex flex-row items-center justify-center ${
                type === 'prediction'
                  ? 'text-white bg-sky-550'
                  : 'text-bluegray-700 bg-gray-300'
              }`}>
              予想
            </div>
          </div>
          <div
            className="rounded-t overflow-hidden cursor-pointer"
            onClick={() => goDividendDeposit()}>
            <div
              className={`py-2 font-bold flex flex-row items-center justify-center ${
                type === 'deposit'
                  ? 'text-white bg-sky-550'
                  : 'text-bluegray-700 bg-gray-300'
              }`}>
              入金
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const setSessionStoragePredictionType = (type: number): void => {
  const dividend = getDividendStorage();
  const newDividend = { ...dividend, predictionType: type };
  setDividendStorage(newDividend);
};
const setSessionStoragePredictionPeriod = (type: number): void => {
  const dividend = getDividendStorage();
  const newDividend = { ...dividend, predictionPeriod: type };
  setDividendStorage(newDividend);
};

const getDateStates = (dividendData: DividendProps[]) => {
  const haitoYmList = Array.from(
    new Set(dividendData.map(data => data.haitoYm))
  );
  const haitoYearList = Array.from(
    new Set(
      haitoYmList.map(data => {
        const date = convertyyyymmToDate(data);
        return formatDate(date, 'yyyy');
      })
    )
  );
  const sortedHaitoYearList = haitoYearList.sort((first, second) => {
    if (Number(first) > Number(second)) {
      return 1;
    } else if (Number(first) < Number(second)) {
      return -1;
    } else {
      return 0;
    }
  });

  return sortedHaitoYearList;
};

const getTargetDividendData = (dividendData: DividendProps[], date: string) => {
  return dividendData.filter(data => {
    const haitoDate = convertyyyymmToDate(data.haitoYm);
    const year = formatDate(haitoDate, 'yyyy');
    return year === date;
  });
};

export const displayTypes = {
  推移: 1,
  内訳: 2,
} as const;
export type DisplayType = typeof displayTypes[keyof typeof displayTypes];

const DividendPredictionInternal = ({
  dividendData,
}: {
  dividendData: DividendProps[];
}) => {
  const dividendStorage = getDividendStorage();
  const initialDisplayType =
    dividendStorage === null ||
    !dividendStorage.predictionType ||
    dividendStorage.predictionType === displayTypes.内訳
      ? displayTypes.内訳
      : displayTypes.推移;

  const [displayType, setDisplayType] = useState<DisplayType>(
    initialDisplayType
  );

  const updateDisplayType = (type: DisplayType): void => {
    setDisplayType(type);
    setSessionStoragePredictionType(
      type === displayTypes.推移 ? displayTypes.推移 : displayTypes.内訳
    );
  };

  const dateStates = getDateStates(dividendData);

  const predictionPeriod =
    dividendStorage === null || !dividendStorage.predictionPeriod
      ? dateStates[0]
      : dividendStorage.predictionPeriod.toString();
  const isPredictionPeriodValid = dateStates.includes(predictionPeriod);

  const initialSelectedDate = isPredictionPeriodValid
    ? predictionPeriod
    : dateStates[0];
  const [selectedDate, setSelectedDate] = useState<string>(initialSelectedDate); // yyyy

  const updateSelectedDate = (type: string): void => {
    setSelectedDate(type);
    setSessionStoragePredictionPeriod(Number(type));
  };

  const [targetYearDividendData, setTargetYearDividendData] = useState<
    DividendProps[] | null
  >(null);
  const [totalDividend, setTotalDividend] = useState<number | null>(null);
  const [isTaxDeduction, setIsTaxDeduction] = useState<boolean>(false);
  const [isFirst, setIsFirst] = useState<boolean>(true);

  useEffect(() => {
    const updateData = (targetDate: string) => {
      const targetYearDividendData = getTargetDividendData(
        dividendData,
        targetDate
      );
      setTargetYearDividendData(targetYearDividendData);

      const totalDividend = getSummaryDividendHaito(
        targetYearDividendData,
        isTaxDeduction
      );
      setTotalDividend(totalDividend);
    };

    if (isFirst) {
      const oldestDividendDate_ = dateStates.find(state => {
        return dividendData.map(data => data.haitoYm).includes(state);
      });
      const oldestDividendDate = oldestDividendDate_ ?? dateStates[0];

      updateData(oldestDividendDate);
      setIsFirst(false);
    } else {
      updateData(selectedDate);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, displayType]);

  useEffect(() => {
    const targetYearDividendData = getTargetDividendData(
      dividendData,
      selectedDate
    );
    setTargetYearDividendData(targetYearDividendData);

    const totalDividend = getSummaryDividendHaito(
      targetYearDividendData,
      isTaxDeduction
    );
    setTotalDividend(totalDividend);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTaxDeduction]);

  if (targetYearDividendData === null || totalDividend === null)
    return (
      <DividendPredictionLoading
        str={displayType === displayTypes.推移 ? '月' : '銘柄'}
      />
    );

  return (
    <>
      <DividendHeader {...{ type: 'prediction' }} />
      <div className="bg-white">
        <div className="relative flex justify-center items-center pt-5 mx-4 text-bluegray-700">
          <div
            className="relative w-full flex flex-row justify-center items-center mx-auto"
            style={{ width: '110px' }}>
            <select
              className={`h-full w-full pl-2 appearance-none bg-transparent text-base font-bold cursor-pointer`}
              value={selectedDate}
              onChange={(e): void => {
                updateSelectedDate(e.currentTarget.value);
              }}>
              {dateStates.map((s, i) => {
                return (
                  <option key={i} value={s.toString()}>
                    {s}年
                  </option>
                );
              })}
            </select>
            <div
              className={`absolute icon-arrow_drop_down pointer-events-none p-0.5 text-2xl`}
              style={{ right: '6%' }}></div>
          </div>
          <div
            className="absolute flex flex-col justify-center items-center"
            style={{ right: '2%' }}>
            <div className="text-1.5xs">税引き計算</div>
            <div className="toggle ml-2">
              <div className="checkbox">
                <input
                  className="toggle-lg"
                  type="checkbox"
                  checked={isTaxDeduction}
                  onChange={() => setIsTaxDeduction(prev => !prev)}
                />
                <label className="toggle-lg" htmlFor={``}></label>
              </div>
            </div>
          </div>
        </div>
        <div className="px-4 mt-2 text-center text-xs text-bluegray-700">
          <span className="mr-1 text-2xl font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>
        <div className="flex flex-row justify-center items-center pt-2 text-sm text-gray-500">
          <div
            className={`px-3 ${
              displayType === displayTypes.内訳
                ? 'text-white bg-blue-700 rounded'
                : ''
            } cursor-pointer`}
            onClick={(): void => updateDisplayType(displayTypes.内訳)}>
            内訳
          </div>
          <div className="text-base">｜</div>
          <div
            className={`px-3 ${
              displayType === displayTypes.推移
                ? 'text-white bg-blue-700 rounded'
                : ''
            } cursor-pointer`}
            onClick={(): void => updateDisplayType(displayTypes.推移)}>
            推移
          </div>
        </div>
        {displayType === displayTypes.推移 ? (
          <DividendTransition
            {...{
              dividendData: targetYearDividendData,
              selectedDate,
              isTaxDeduction,
            }}
          />
        ) : (
          <DividendClassification
            {...{
              dividendData: targetYearDividendData,
              selectedDate,
              totalDividend,
              isTaxDeduction,
            }}
          />
        )}
      </div>
      <UpdateTimeComment />
      <HightDividendList {...{ tag: 'prediction', className: 'mt-8 ml-4' }} />
    </>
  );
};

const GetDividendData = () => {
  const { data } = useQuery('getHaitoDividendPrediction', () => getHaitoAPI(), {
    refetchOnWindowFocus: false,
  });

  if (data === undefined)
    return (
      <Loading
        style={{ marginTop: '100px', marginBottom: '100px' }}
        loading={true}
      />
    );
  if (data.length === 0) return <DividendNoData />;
  const dividendData = data.filter((data: DividendProps) => data.haito !== 0);

  return <DividendPredictionInternal {...{ dividendData }} />;
};

const modalID = 'dividendQuestionary' as ModalID;

const DividendPrediction = (): React.ReactElement => {
  if (isIosApp()) return <Redirect to="/home" />;

  return (
    <PremiumCheck content={<DevidendPremium />}>
      {isPremium() && <RecurrentModal {...{ modalID }} />}
      <GetDividendData />
    </PremiumCheck>
  );
};

export type DividendProps = {
  symbol: string;
  symbolName: string;
  haito: number;
  taxExcludedHaito: number;
  qty: number;
  accountType: number;
  haitoYm: string; // yyyymm
  unreleased: boolean;
  hasNew: boolean;
};

const getHaitoAPI = async () => {
  const ret_ = await getHaito();
  const ret = ret_.Items.map(item => {
    return {
      symbol: item.Symbol,
      symbolName: item.SymbolName,
      haito: item.Haito,
      taxExcludedHaito: item.TaxExcludedHaito,
      qty: item.Qty,
      accountType: item.AccountType,
      // memo: apiレスポンスを yyyy.mm -> yyyymm に変換する
      haitoYm: item.HaitoYm.replace('.', ''),
      unreleased: item.Unreleased,
      hasNew: item.HasNew,
    };
  }) as DividendProps[];

  return ret;
};

export default DividendPrediction;
